import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { AttendanceCheckinPanel } from '@/pages/attendance/attendance-checkin-panel';
import { AttendanceSubnav } from '@/pages/attendance/attendance-subnav';
import { formatCheckinWhenLine } from '@/pages/attendance/checkin-occurrence-utils';
import { usePreferences } from '@/stores/preferences-store';

interface SessionOccurrence {
  programName: string;
  programKind: 'service' | 'program';
  occurrenceDate: string;
  startsAt: string;
  endsAt: string;
  venueName: string | null;
  programStartsOn: string | null;
  programEndsOn: string | null;
}

interface Session {
  id: string;
  occurrenceId: string;
  method: string;
  status: string;
  headcount: number;
  kiosks: Array<{ id: string; name: string }>;
  occurrence: SessionOccurrence | null;
}

export const SessionsPage = () => {
  const { t } = useTranslation('attendance');
  const { t: tCommon } = useTranslation();
  const { t: tPrograms } = useTranslation('programs');
  const { locale } = usePreferences();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const kindLabel = (kind: 'service' | 'program') => tPrograms(`calendar.kind.${kind}`);
  const multiDayRangeLabel = (start: string, end: string) =>
    t('sessions.multiDayRange', { start, end });

  const list = useQuery({
    queryKey: ['attendance-sessions', 'history', page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      return api.get<{
        data: Session[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/attendance/sessions?${params}`);
    },
  });

  const columns: DataTableColumn<Session>[] = [
    {
      id: 'when',
      header: t('sessions.columns.when'),
      cell: (row) => {
        const occ = row.occurrence;
        if (!occ) return <span className="text-body-sm text-fg-secondary">—</span>;
        return (
          <span className="text-body-sm text-fg">
            {formatCheckinWhenLine(
              {
                id: row.occurrenceId,
                programId: '',
                programName: occ.programName,
                programKind: occ.programKind,
                programStartsOn: occ.programStartsOn,
                programEndsOn: occ.programEndsOn,
                occurrenceDate: occ.occurrenceDate,
                startsAt: occ.startsAt,
                endsAt: occ.endsAt,
                venueName: occ.venueName,
                dayDetailId: null,
              },
              locale,
              multiDayRangeLabel,
            )}
          </span>
        );
      },
    },
    {
      id: 'service',
      header: t('sessions.columns.service'),
      cell: (row) => {
        const occ = row.occurrence;
        if (!occ) return <span className="text-body-sm text-fg-secondary">—</span>;
        const kind = occ.programKind ?? 'service';
        return (
          <span className="text-body-sm font-medium text-fg">
            {occ.programName.trim() || kindLabel(kind)}
          </span>
        );
      },
    },
    {
      id: 'method',
      header: t('sessions.columns.method'),
      cell: (row) => t(`sessions.method.${row.method}`, row.method),
    },
    {
      id: 'status',
      header: t('sessions.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'open' ? 'success' : 'neutral'}>
          {row.status === 'open' ? t('sessions.statusOpen') : t('sessions.statusClosed')}
        </Badge>
      ),
    },
    {
      id: 'screens',
      header: t('sessions.columns.screens'),
      cell: (row) =>
        row.kiosks?.length ? (
          <span className="text-body-sm text-fg">{row.kiosks.map((kiosk) => kiosk.name).join(', ')}</span>
        ) : (
          <span className="text-body-sm text-fg-secondary">—</span>
        ),
    },
    {
      id: 'headcount',
      header: t('sessions.columns.headcount'),
      cell: (row) => row.headcount,
      align: 'right',
    },
  ];

  const rows = list.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('sessions.title')}
        description={t('sessions.description')}
        tipId="attendance.sessionsVsKiosks"
      />
      <AttendanceSubnav />

      <AttendanceCheckinPanel />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('sessions.historyEmpty')}</p>}
        onRowClick={(row) => void navigate(`/attendance/sessions/${row.id}`)}
        actionsHeader={tCommon('table.actions')}
      />

      {list.data?.page && list.data.page.totalPages > 1 ? (
        <Pagination
          page={list.data.page.page}
          pageSize={list.data.page.pageSize}
          totalItems={list.data.page.totalItems}
          totalPages={list.data.page.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}
    </div>
  );
};
